import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'elyvora:wishlist';

/**
 * Event fired whenever the wishlist contents change.
 * @extends {Event}
 */
export class WishlistUpdateEvent extends Event {
  /**
   * Creates a new WishlistUpdateEvent
   * @param {string[]} handles - The product handles currently in the wishlist
   */
  constructor(handles) {
    super(WishlistUpdateEvent.eventName, { bubbles: true });
    this.detail = { handles };
  }

  static eventName = 'wishlist:update';
}

/** @returns {string[]} */
function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/** @param {string[]} handles */
function writeWishlist(handles) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  document.dispatchEvent(new WishlistUpdateEvent(handles));
}

/** @param {string} handle */
export function isWishlisted(handle) {
  return readWishlist().includes(handle);
}

/** @param {string} handle */
export function toggleWishlisted(handle) {
  const handles = readWishlist();
  const index = handles.indexOf(handle);

  if (index === -1) {
    handles.push(handle);
  } else {
    handles.splice(index, 1);
  }

  writeWishlist(handles);
  return handles.includes(handle);
}

export function getWishlistHandles() {
  return readWishlist();
}

/** @param {unknown} value */
function escapeHtml(value) {
  const str = String(value ?? '');
  return str.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return char;
    }
  });
}

/**
 * Heart toggle button rendered on product cards.
 */
class WishlistToggleButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
  }

  toggle = () => {
    const handle = this.dataset.productHandle;
    if (!handle) return;
    toggleWishlisted(handle);
  };

  /**
   * Handles the wishlist update event.
   * @param {Event} _event - The wishlist update event.
   */
  #onUpdate = (_event) => {
    this.#syncState();
  };

  #syncState() {
    const handle = this.dataset.productHandle;
    const active = Boolean(handle) && isWishlisted(/** @type {string} */ (handle));
    this.classList.toggle('is-active', active);
    this.setAttribute('aria-pressed', String(active));

    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.setAttribute('aria-label', label);
  }
}

/**
 * Header icon showing the current wishlist item count.
 *
 * @typedef {object} IconRefs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<IconRefs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
    this.#render(getWishlistHandles().length);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
  }

  /**
   * Handles the wishlist update event.
   * @param {WishlistUpdateEvent} event - The wishlist update event.
   */
  #onUpdate = (event) => {
    this.#render(event.detail.handles.length);
  };

  /** @param {number} count */
  #render(count) {
    this.refs.wishlistBubbleCount.textContent = count > 0 ? String(count) : '';
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
  }
}

/**
 * Drawer listing the saved products, opened from the header wishlist icon.
 *
 * @extends {DialogComponent}
 */
class WishlistDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
  }

  open = () => {
    this.showDialog();
    this.#renderList();
  };

  close = () => {
    this.closeDialog();
  };

  /**
   * Removes a product from the wishlist.
   * @param {{handle?: string}} data - The data parsed from the `on:click` binding.
   */
  removeItem = (data) => {
    if (data?.handle) toggleWishlisted(data.handle);
  };

  /**
   * Handles the wishlist update event.
   * @param {Event} _event - The wishlist update event.
   */
  #onUpdate = (_event) => {
    if (this.refs.dialog?.open) this.#renderList();
  };

  async #renderList() {
    const handles = getWishlistHandles();
    const list = /** @type {HTMLElement | null} */ (this.querySelector('.wishlist-drawer__list'));
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('.wishlist-drawer__empty'));

    if (!list || !empty) return;

    if (handles.length === 0) {
      list.replaceChildren();
      empty.hidden = false;
      return;
    }

    empty.hidden = true;

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch (_) {
          return null;
        }
      })
    );

    const fragment = document.createDocumentFragment();

    products.forEach((product, index) => {
      const handle = handles[index];
      if (!product || !handle) return;

      const item = document.createElement('div');
      item.className = 'wishlist-drawer__item';
      item.innerHTML = `
        <a href="/products/${escapeHtml(product.handle)}" class="wishlist-drawer__item-image">
          <img src="${escapeHtml(product.featured_image ?? '')}" alt="${escapeHtml(product.title)}" width="80" height="80" loading="lazy">
        </a>
        <div class="wishlist-drawer__item-details">
          <a href="/products/${escapeHtml(product.handle)}" class="wishlist-drawer__item-title">${escapeHtml(product.title)}</a>
          <span class="wishlist-drawer__item-price">${escapeHtml(formatMoney(product.price, moneyFormat, currency))}</span>
        </div>
        <button
          type="button"
          class="wishlist-drawer__item-remove button-unstyled"
          on:click="/removeItem?handle=${encodeURIComponent(handle)}"
          aria-label="Remove"
        >&times;</button>
      `;
      fragment.appendChild(item);
    });

    list.replaceChildren(fragment);
  }
}

if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggleButton);
if (!customElements.get('wishlist-icon')) customElements.define('wishlist-icon', WishlistIcon);
if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
